import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Alert, Button, SectionCard } from "@/deps/ui-library";
import {
  useCancelPurchaseOrder,
  useCompanyProfile,
  useConfirmPurchaseOrder,
  useConvertPurchaseOrder,
  useNumberPurchaseOrder,
  usePurchaseOrder,
  useSendPurchaseOrder,
} from "@/api/queries";
import { useCan, useDocumentMailEnabled, useTaxWords } from "@/app/session";
import { useToast } from "@/app/toasts";
import { Dialog } from "@/components/Dialog";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { DocumentEmails } from "@/components/DocumentEmails";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { DraftActions } from "@/components/DraftActions";
import { GoodsReceiptPrint, PurchaseOrderPrint } from "@/components/print/PurchaseOrderPrint";
import { PrintOverlay } from "@/components/print/PrintOverlay";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { calculateTotals } from "@/lib/invoiceTotals";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { PurchaseOrderStatusBadge } from "./PurchaseOrderStatusBadge";
import { ReceiveItemsCard } from "./ReceiveItemsCard";

const NOTE: Partial<Record<string, string>> = {
  partiallyReceived: "Bill what has arrived, or wait for the rest.",
  received:
    "Everything on the order has arrived. Convert it to a bill when the supplier's invoice comes in.",
  billed: "Billed in full. The order is kept as the record of what was agreed.",
  cancelled: "Cancelled. It's kept for the record, and nothing was posted.",
};

export function PurchaseOrderDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/purchases/orders/$id" });
  const navigate = useNavigate();
  const { data, isPending, error } = usePurchaseOrder(id);
  const { data: seller } = useCompanyProfile();
  const number = useNumberPurchaseOrder();
  const send = useSendPurchaseOrder();
  const confirm = useConfirmPurchaseOrder();
  const cancel = useCancelPurchaseOrder();
  const convert = useConvertPurchaseOrder();
  const can = useCan();
  const mailEnabled = useDocumentMailEnabled();
  const toast = useToast();
  const held = useApprovalHoldsDraft("purchaseOrder", id, data?.status === "draft");
  const [printing, setPrinting] = useState<"order" | "receipt" | null>(null);
  const [converting, setConverting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [emailing, setEmailing] = useState(false);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading purchase order…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That purchase order could not be loaded.</p>;

  const mayWrite = can("purchases.orders.create");
  const draft = data.status === "draft";
  const open = data.status === "sent" || data.status === "confirmed";
  const receiving = open || data.status === "partiallyReceived";
  const toBill = data.lines.filter((line) => Number(line.received) > Number(line.billed));
  const canCancel =
    mayWrite && !held && (draft || open) && data.lines.every((line) => Number(line.received) === 0);
  const totals = calculateTotals(
    data.lines.map((line) => ({
      category: Number(line.vatRatePct) > 0 ? ("S" as const) : ("Z" as const),
      rate: line.vatRatePct,
      grossPrice: line.unitPrice,
      quantity: line.quantity,
    })),
  );
  const failure = number.error ?? send.error ?? confirm.error ?? cancel.error ?? convert.error;
  const note = NOTE[data.status];

  /** With email set up, the dialog is how it goes, and the mail says the number. */
  const issue = () => {
    if (mailEnabled && mayWrite) number.mutate(id, { onSuccess: () => setEmailing(true) });
    else send.mutate(id);
  };

  return (
    <>
      <PrintOverlay
        open={printing === "order"}
        onClose={() => setPrinting(null)}
        title={`Purchase order ${data.number ?? ""}`.trim()}
      >
        <PurchaseOrderPrint order={data} seller={seller} />
      </PrintOverlay>

      <PrintOverlay
        open={printing === "receipt"}
        onClose={() => setPrinting(null)}
        title={`Goods receipt ${data.number ?? ""}`.trim()}
      >
        <GoodsReceiptPrint order={data} seller={seller} />
      </PrintOverlay>

      {emailing ? (
        <SendEmailDialog
          target={{ kind: "purchaseOrder", id }}
          onClose={() => setEmailing(false)}
          onSent={(recipients) => {
            setEmailing(false);
            toast({
              title: `${data.number ?? "The order"} is on its way to ${recipients.join(", ")}.`,
              scheme: "success",
            });
          }}
        />
      ) : null}

      <Dialog
        open={converting}
        onClose={() => setConverting(false)}
        title={`Convert ${data.number ?? "this order"} to a bill`}
        footer={
          <>
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => setConverting(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={convert.isPending}
              onClick={() =>
                convert.mutate(id, {
                  onSuccess: (bill) =>
                    void navigate({ to: "/purchases/bills/$id", params: { id: bill } }),
                  onSettled: () => setConverting(false),
                })
              }
            >
              {convert.isPending ? "Creating" : "Create the bill"}
            </Button>
          </>
        }
      >
        <p className="text-fg text-sm">
          This makes a draft bill from {data.supplierName} for what has arrived and hasn't been
          billed, on {toBill.length} {toBill.length === 1 ? "line" : "lines"}.{" "}
          {data.status === "partiallyReceived"
            ? "The order stays open for the rest, which can be billed later. "
            : ""}
          Replace its reference with the supplier's invoice number. Withholding is applied when it's
          paid.
        </p>
      </Dialog>

      <Dialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        title={`Cancel ${data.number ?? "this order"}?`}
        footer={
          <>
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => setCancelling(false)}
            >
              Keep it
            </Button>
            <Button
              colorScheme="danger"
              variant="solid"
              size="sm"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(id, { onSettled: () => setCancelling(false) })}
            >
              Cancel the order
            </Button>
          </>
        }
      >
        <p className="text-fg text-sm">
          It's kept for the record, nothing was posted, and it can't be undone.
          {data.status !== "draft" ? " Tell the supplier yourself: this doesn't email them." : ""}
        </p>
      </Dialog>

      <DocumentScreen
        title="Purchase order"
        number={data.number}
        badge={<PurchaseOrderStatusBadge status={data.status} />}
        subtitle={`${data.supplierName} · expected ${formatDate(data.expected)}`}
        actions={
          <span className="flex flex-wrap items-center justify-end gap-2">
            {draft && mayWrite && !held ? (
              <DraftActions
                kind="purchaseOrders"
                label="purchase order"
                id={id}
                onEdit={() => void navigate({ to: "/purchases/orders/$id/edit", params: { id } })}
                onDeleted={() => void navigate({ to: "/purchases/orders" })}
              />
            ) : null}
            {canCancel && !draft ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="sm"
                onClick={() => setCancelling(true)}
              >
                Cancel order
              </Button>
            ) : null}
            {data.number ? (
              <DownloadPdfButton kind="purchaseOrder" id={id} number={data.number} />
            ) : null}
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => setPrinting("order")}
            >
              Print
            </Button>
            {data.receipts.length > 0 ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => setPrinting("receipt")}
              >
                Print goods receipt
              </Button>
            ) : null}
            {!draft && data.status !== "cancelled" && mailEnabled && mayWrite ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => setEmailing(true)}
              >
                Email order
              </Button>
            ) : null}
            {data.status === "sent" && mayWrite ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(id)}
              >
                Mark confirmed
              </Button>
            ) : null}
            {draft ? (
              <ApprovalActions
                kind="purchaseOrder"
                id={id}
                canCreate={mayWrite}
                canIssue={can("purchases.orders.send")}
                issuing={send.isPending || number.isPending}
                onIssue={issue}
                issueLabel="Send to supplier"
                approveLabel="Approve and send"
                workingLabel="Sending"
              />
            ) : null}
            {toBill.length > 0 && can("purchases.bills.create") ? (
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                onClick={() => setConverting(true)}
              >
                Convert to bill
              </Button>
            ) : null}
          </span>
        }
        facts={[
          { label: "Supplier", value: data.supplierName },
          { label: "Order date", value: formatDate(data.date) },
          { label: "Expected", value: formatDate(data.expected) },
          ...(data.deliverTo ? [{ label: "Deliver to", value: data.deliverTo }] : []),
          ...(data.reference ? [{ label: "Reference", value: data.reference }] : []),
          ...(data.sentAt ? [{ label: "Sent", value: formatDate(data.sentAt.slice(0, 10)) }] : []),
          ...(data.status !== "draft" && data.status !== "cancelled"
            ? [{ label: "Received", value: `${data.linesReceived} of ${data.lineCount} lines` }]
            : []),
          ...data.bills.map((bill, index) => ({
            label: index === 0 ? "Billed as" : "And as",
            value: (
              <Link
                to="/purchases/bills/$id"
                params={{ id: bill.id }}
                className="num text-primary underline"
              >
                {bill.number ?? "Draft bill"}
              </Link>
            ),
          })),
          { label: "Amount", value: <span className="num">{formatAmount(data.total)}</span> },
        ]}
        history={data.history}
        aside={
          <div className="flex flex-col gap-4">
            <ApprovalCard kind="purchaseOrder" id={id} />
            <DocumentEmails of={{ kind: "purchaseOrder", id }} />
          </div>
        }
      >
        {failure ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(failure as Error).message}
          />
        ) : null}

        {note ? (
          <Alert config={{ variant: "subtle", colorScheme: "info" }} description={note} />
        ) : null}

        {receiving && mayWrite ? <ReceiveItemsCard order={data} /> : null}

        <SectionCard title="Lines" flush>
          <LinesTable
            caption={`Lines of purchase order ${data.number ?? "draft"}`}
            lines={data.lines.map((line) => ({
              n: line.n,
              label: line.description,
              account: line.accountName,
              quantity:
                draft || data.status === "cancelled"
                  ? line.quantity
                  : `${line.received} of ${line.quantity}`,
              unitPrice: formatAmount(line.unitPrice),
              tax: `${line.vatRatePct}%`,
              net: formatAmount(line.lineNet),
            }))}
          />
        </SectionCard>

        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard title={say("VAT breakdown")}>
            <VatBreakdownTable totals={totals} />
          </SectionCard>
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} showDue={false} />
          </SectionCard>
        </div>

        {data.terms || data.note ? (
          <SectionCard title="Terms and note">
            <div className="flex flex-col gap-2 text-sm">
              {data.terms ? <p className="text-fg whitespace-pre-line">{data.terms}</p> : null}
              {data.note ? <p className="text-fg-muted whitespace-pre-line">{data.note}</p> : null}
            </div>
          </SectionCard>
        ) : null}
      </DocumentScreen>
    </>
  );
}
